import { useState } from 'react';
import { X } from 'lucide-react';
import { apiUtils } from '../../../hooks/useApi';
import { API_ENDPOINTS } from '../../../services/apiEndpoints';
import {
  useHotelQuestionnaireAnswers,
  useHotelQuestionnaireSteps,
  useHotelSources,
} from '../../../hooks/useHotelQuestionnaireHooks';
import {
  Container,
  ModalHeader,
  ModalTitle,
  CloseButton,
  Description,
  FileCountContainer,
  FileCountText,
  SaveButtonContainer,
  SaveButton,
  ConfirmationModalContent,
  ConfirmationTitle,
  ConfirmationMessage,
  ConfirmationButtons,
  ConfirmationButton,
} from './FileUpload.styled';
import UploadZone from './UploadZone';
import FileManager, { FileUploadState } from './FileManager';
import FilePreview from './FilePreview';
import { Modal } from '../Modal';
import { Attachment } from '../../../types';
import {
  convertAttachmentsToFileUploadState,
  createAttachmentObject,
  updateQuestionAttachmentsIfNeeded,
} from './attachmentUtils';
import { isImageFile, validateFileSize, formatFileSize } from './fileUtils';
import { useToast } from '../../../hooks';
import { useEffect } from 'react';

interface FileUploadPopUpProps {
  questionId: string | undefined;
  question?: any;
  onClose?: (refetch?: boolean) => void;
  maxFiles: number;
  maxFileSize?: number; // in MB
  simplified?: boolean;
  attachments: Attachment[] | [];
  onUploadCompleteInSources?: (questionId: string) => void;
  disabled?: boolean;
}

export default function FileUploadPopUp({
  questionId,
  question,
  onClose,
  maxFiles = 2,
  maxFileSize = 20, // 20MB default
  simplified = false,
  attachments = [],
  onUploadCompleteInSources,
  disabled = false,
}: FileUploadPopUpProps) {

  const { error, success } = useToast();
  const { stepsData, currentStepKey } = useHotelQuestionnaireSteps();
  const { answers, draftAnswers } = useHotelQuestionnaireAnswers();
  const { updateQuestionAttachments, updateQuestionUploadedFilenames } =
    useHotelSources();

  const [uploadedFiles, setUploadedFiles] = useState<FileUploadState[]>(
    convertAttachmentsToFileUploadState(attachments)
  );
  const [previewFile, setPreviewFile] = useState<FileUploadState | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Initialize uploaded filenames when component mounts with existing attachments
  useEffect(() => {
    if (questionId && attachments.length > 0) {
      const existingFilenames = attachments
        .map(attachment => attachment.original_filename || attachment.fileName)
        .filter((filename): filename is string => Boolean(filename));

      if (existingFilenames.length > 0) {
        console.log('Initializing uploaded filenames:', {
          questionId,
          isRadioGrid: question?.component === 'radio-grid',
          existingFilenames,
        });
        updateQuestionUploadedFilenames(questionId, existingFilenames);
      }
    }
  }, [questionId, attachments, updateQuestionUploadedFilenames, question?.component]);
  // Add this function to remove highlight
  const removeHighlightFromQuestion = () => {
    console.log('Removing highlight from question:', questionId);
    if (questionId && onUploadCompleteInSources) {
      onUploadCompleteInSources(questionId);
    }
  };

  // Validate if question has an answer
  const validateQuestionAnswer = (): boolean => {
    if (currentStepKey === 'sources' && question) {
      console.log('validation sources one');
      
      // For radio-grid questions, check if any subquestion has an answer
      if (question.component === 'radio-grid' && question.subQuestions) {
        const hasSubQuestionAnswer = question.subQuestions.some(
          (subQ: any) => 
            answers[subQ.id] !== undefined || 
            draftAnswers[subQ.id] !== undefined ||
            subQ.defaultValue !== null
        );
        
        if (hasSubQuestionAnswer) {
          return true;
        }
      }
      
      return question.defaultValue !== null;
    }
    if (!questionId || !stepsData) {
      console.warn('Question ID or steps data not available');
      return false;
    }

    // Check if question has an answer (both in draft and the main answers state)
    const hasAnswer =
      answers[questionId] !== undefined ||
      draftAnswers[questionId] !== undefined;

    return hasAnswer;
  };

  // Upload single file to API
  const uploadSingleFileToApi = async (
    file: File,
    fileId: string
  ): Promise<any> => {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('hotel_id', stepsData?.hotelId || '');
    formData.append('question_id', questionId || '');

    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.hotels.uploadFiles,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent: any) => {
            if (progressEvent.total) {
              const progress = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
              );

              // Update file progress in state
              setUploadedFiles(prev =>
                prev.map(f =>
                  f.id === fileId
                    ? { ...f, progress, status: 'uploading' as const }
                    : f
                )
              );
            }
          },
        }
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  // Delete file from API
  const deleteFileFromApi = async (attachmentId: string): Promise<void> => {
    try {
      await apiUtils.delete(
        API_ENDPOINTS.hotels.deleteAttachment(attachmentId)
      );
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      throw error;
    }
  };

  // Create image preview
  const createImagePreview = (file: File): Promise<string> => {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = e => resolve(e.target?.result as string);
      reader.readAsDataURL(file);
    });
  };

  // Add files to pending state (no API call yet)
  const handleFileUpload = async (files: FileList) => {
    if (disabled) {
      error('Upload is disabled for this question. Please select a different answer option.');
      return;
    }



    if (!validateQuestionAnswer()) {
      error(
        `Please provide an answer to the question in section before uploading files.`
      );
      return;
    }

    const filesArray = Array.from(files);

    // Validate file sizes
    const invalidFiles = filesArray.filter(
      file => !validateFileSize(file, maxFileSize)
    );

    if (invalidFiles.length > 0) {
      const invalidFileNames = invalidFiles
        .map(file => `${file.name} (${formatFileSize(file.size)})`)
        .join(', ');

      error(
        `The following files exceed the maximum size limit of ${maxFileSize}MB: ${invalidFileNames}`
      );
      return;
    }

    // Check if adding these files would exceed the max limit
    if (uploadedFiles.length + filesArray.length > maxFiles) {
      error(`Cannot upload more than ${maxFiles} files in total.`);
      return;
    }

    try {
      // Create file states for immediate display
      const newFileStates: FileUploadState[] = await Promise.all(
        filesArray.map(async (file, index) => {
          let preview: string | undefined;
          if (isImageFile(file.type)) {
            preview = await createImagePreview(file);
          }

          return {
            id: `${Date.now()}_${index}`,
            file,
            status: 'initial' as const,
            progress: 0,
            preview,
          };
        })
      );

      // Add to uploaded files state
      setUploadedFiles(prev => [...prev, ...newFileStates]);
    } catch (error) {
      console.error('File preparation failed:', error);
    }
  };

  // Upload files individually with progress tracking
  const handleSave = async () => {
    // Get files that are in initial state (ready to upload)
    const filesToUpload = uploadedFiles.filter(f => f.status === 'initial');

    if (filesToUpload.length === 0) {
      success('No new files to upload.');
      return;
    }

    setIsUploading(true);

    // Set all files to pending state initially
    setUploadedFiles(prev =>
      prev.map(f =>
        f.status === 'initial' ? { ...f, status: 'pending' as const } : f
      )
    );

    const uploadPromises = filesToUpload.map(async fileState => {
      try {
        // Start upload for this file
        const result = await uploadSingleFileToApi(
          fileState.file,
          fileState.id
        );

        // Handle successful upload
        const uploadedFileData = result?.data?.data.uploaded_files?.[0];

        if (uploadedFileData) {
          // Update file state to completed
          setUploadedFiles(prev =>
            prev.map(f =>
              f.id === fileState.id
                ? {
                    ...f,
                    status: 'completed' as const,
                    progress: 100,
                    uploadResult: result,
                    attachmentId: uploadedFileData.attachment_id,
                    previewUrl: uploadedFileData.preview_url,
                  }
                : f
            )
          );

          return {
            success: true,
            fileId: fileState.id,
            fileName: fileState.file.name,
            attachmentData: uploadedFileData,
            originalFilename: uploadedFileData.original_filename,
          };
        } else {
          throw new Error('No upload data received');
        }
      } catch (error: any) {
        console.error(`Upload failed for file ${fileState.file.name}:`, error);

        // Update file state to error
        setUploadedFiles(prev =>
          prev.map(f =>
            f.id === fileState.id
              ? {
                  ...f,
                  status: 'error' as const,
                  error:
                    error.response?.data?.message ||
                    error.message ||
                    'Upload failed',
                  progress: 0,
                }
              : f
          )
        );

        return {
          success: false,
          fileId: fileState.id,
          fileName: fileState.file.name,
          error:
            error.response?.data?.message || error.message || 'Upload failed',
        };
      }
    });

    try {
      // Wait for all uploads to complete
      const results = await Promise.allSettled(uploadPromises);

      // Process results
      const successfulUploads = results
        .filter(
          (result): result is PromiseFulfilledResult<any> =>
            result.status === 'fulfilled' && result.value.success
        )
        .map(result => result.value);
      console.log('Successful uploads:', successfulUploads);
      //remove the highlight class from question after successful upload
      if (successfulUploads.length > 0) {
        removeHighlightFromQuestion();
      }

      const failedUploads = results
        .filter(
          (result): result is PromiseFulfilledResult<any> =>
            result.status === 'fulfilled' && !result.value.success
        )
        .map(result => result.value);

      // Update sources data if we're in sources step and have successful uploads
      if (
        currentStepKey === 'sources' &&
        questionId &&
        successfulUploads.length > 0
      ) {
        setUploadedFiles(currentFiles => {
          const completedFiles = currentFiles.filter(
            f => f.status === 'completed' && f.attachmentId
          );
          const allAttachments = completedFiles.map(f =>
            createAttachmentObject(f)
          );
          console.log('All Attachments to update:', allAttachments);
          updateQuestionAttachments(questionId, allAttachments);
          return currentFiles;
        });
      }

      // Update uploaded filenames after all uploads are complete
      if (questionId && successfulUploads.length > 0) {
        // Collect filenames from successful uploads
        const newlyUploadedFilenames = successfulUploads
          .map(upload => upload.originalFilename)
          .filter(Boolean);

        // Get existing completed files filenames (excluding the ones we just uploaded)
        const existingCompletedFilenames = uploadedFiles
          .filter(
            f =>
              f.status === 'completed' &&
              !successfulUploads.some(upload => upload.fileId === f.id)
          )
          .map(f => {
            // For existing files (from attachments), try to get original_filename
            const existingAttachment = attachments.find(
              att =>
                att.attachment_id === f.attachmentId ||
                att.original_filename === f.file.name ||
                att.fileName === f.file.name
            );
            return existingAttachment?.original_filename || f.file.name;
          })
          .filter(Boolean);

        // Combine all filenames
        const allCompletedFilenames = [
          ...existingCompletedFilenames,
          ...newlyUploadedFilenames,
        ];

        console.log('Updating uploaded filenames after batch completion:', {
          questionId,
          isRadioGrid: question?.component === 'radio-grid',
          newlyUploaded: newlyUploadedFilenames,
          existing: existingCompletedFilenames,
          combined: allCompletedFilenames,
        });

        updateQuestionUploadedFilenames(questionId, allCompletedFilenames);
      }

      // No additional state to clear

      // Show results
      if (successfulUploads.length > 0 && failedUploads.length === 0) {
        success(`Successfully uploaded ${successfulUploads.length} file(s).`);
        // Only close modal if ALL files were successfully uploaded
        onClose?.(true);
      } else if (successfulUploads.length > 0 && failedUploads.length > 0) {
        success(
          `Successfully uploaded ${successfulUploads.length} file(s). ${failedUploads.length} file(s) failed.`
        );
        // Don't close modal if some files failed
      } else if (failedUploads.length > 0) {
        error(`Failed to upload ${failedUploads.length} file(s).`);
        // Don't close modal if all files failed
      }

      // Remove the automatic close for any successful uploads since we want to keep modal open if any failed
    } catch (uploadError) {
      console.error('Upload batch processing failed:', uploadError);
      error('Failed to process file uploads.');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle close with confirmation if there are unsaved changes
  const handleClose = () => {
    const hasInitialFiles = uploadedFiles.some(f => f.status === 'initial');
    if (hasInitialFiles) {
      setShowConfirmationModal(true);
    } else {
      onClose?.(uploadedFiles.some(f => f.status === 'completed'));
    }
  };

  // Confirm close without saving
  const handleConfirmClose = () => {
    setShowConfirmationModal(false);
    onClose?.(uploadedFiles.some(f => f.status === 'completed'));
  };

  // Cancel close and return to modal
  const handleCancelClose = () => {
    setShowConfirmationModal(false);
  };

  const removeFile = async (fileId: string) => {
    // Find the file to get the attachment ID and status
    const fileToRemove = uploadedFiles.find(f => f.id === fileId);

    if (!fileToRemove) return;

    // If file is initial or pending (not uploaded yet), just remove from local state
    if (
      fileToRemove.status === 'initial' ||
      fileToRemove.status === 'pending'
    ) {
      // Remove from uploaded files state
      setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
      return;
    }

    // If file is completed (uploaded), delete from server
    if (fileToRemove.attachmentId) {
      try {
        // Set status to deleting
        setUploadedFiles(prev =>
          prev.map(f =>
            f.id === fileId ? { ...f, status: 'deleting' as const } : f
          )
        );

        await deleteFileFromApi(fileToRemove.attachmentId);
        success(
          `Attachment deleted successfully.`
        );
        console.log('File deleted from server successfully');
      } catch (error) {
        console.error('Failed to delete file from server:', error);
        // Revert status back to completed on error
        setUploadedFiles(prev =>
          prev.map(f =>
            f.id === fileId ? { ...f, status: 'completed' as const } : f
          )
        );
        return; // Don't remove from state if server deletion failed
      }
    }

    // After successful server deletion, update local state and reducer
    setUploadedFiles(prev => {
      const newFiles = prev.filter(f => f.id !== fileId);
      console.log('Updated local files after removal:', newFiles);

      // Update attachments after removal
      updateQuestionAttachmentsIfNeeded(
        currentStepKey || '',
        questionId,
        newFiles,
        updateQuestionAttachments
      );

      // Update uploaded filenames after removal
      if (questionId) {
        const remainingFilenames = newFiles
          .filter(f => f.status === 'completed' && f.file.name)
          .map(f => f.file.name);
        console.log('Updating filenames after removal:', {
          questionId,
          isRadioGrid: question?.component === 'radio-grid',
          remainingFilenames,
        });
        updateQuestionUploadedFilenames(questionId, remainingFilenames);
      }

      return newFiles;
    });
  };

  const viewFile = (file: FileUploadState) => {
    setPreviewFile(file);
    setIsPreviewModalOpen(true);
  };

  const closePreviewModal = () => {
    setIsPreviewModalOpen(false);
    setPreviewFile(null);
  };

  return (
    <Container style={{ 
      // opacity: disabled ? 0.8 : 1,
      // pointerEvents: disabled ? 'none' : 'auto',
    }}>
      {!simplified && (
        <>
          <ModalHeader>
            <ModalTitle>Sources</ModalTitle>
            {/* refetch the current step's questionnaire only if there are uploaded files */}
            <CloseButton onClick={handleClose}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <Description>
            Please confirm you've added sources for the questions below.
          </Description>

          <FileCountContainer>
            <FileCountText>
              Uploaded Files: {uploadedFiles.length}/{maxFiles}
            </FileCountText>
          </FileCountContainer>
        </>
      )}

      <UploadZone
        onFilesSelected={handleFileUpload}
        maxFiles={maxFiles}
        maxFileSize={maxFileSize}
        disabled={isUploading || disabled}
      />

      <FileManager
        files={uploadedFiles}
        onRemoveFile={removeFile}
        onViewFile={viewFile}
        disabled={disabled}
      />
      {/* Save Button */}
      {uploadedFiles.some(f => f.status === 'initial') && (
        <SaveButtonContainer>
          <SaveButton
            onClick={handleSave}
            disabled={
              isUploading || !uploadedFiles.some(f => f.status === 'initial')
            }
          >
            {isUploading
              ? 'Uploading...'
              : `Upload ${
                  uploadedFiles.filter(f => f.status === 'initial').length
                } File${
                  uploadedFiles.filter(f => f.status === 'initial').length > 1
                    ? 's'
                    : ''
                }`}
          </SaveButton>
        </SaveButtonContainer>
      )}

      <FilePreview
        file={previewFile}
        isOpen={isPreviewModalOpen}
        onClose={closePreviewModal}
      />

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmationModal}
        onClose={handleCancelClose}
        size="sm"
      >
        <ConfirmationModalContent>
          <ConfirmationTitle>Unsaved Changes</ConfirmationTitle>
          <ConfirmationMessage>
            You have {uploadedFiles.filter(f => f.status === 'initial').length}{' '}
            unsaved file(s). Are you sure you want to close without saving?
          </ConfirmationMessage>
          <ConfirmationButtons>
            <ConfirmationButton variant="secondary" onClick={handleCancelClose}>
              Cancel
            </ConfirmationButton>
            <ConfirmationButton variant="primary" onClick={handleConfirmClose}>
              Close Without Saving
            </ConfirmationButton>
          </ConfirmationButtons>
        </ConfirmationModalContent>
      </Modal>
    </Container>
  );
}
